<template>
    <div class="relative flex h-64 w-full flex-col items-center justify-center overflow-hidden rounded-md border" id="alarm-chart">
        <div v-if="loading" class="flex h-full items-center justify-center">
            <div class="flex animate-pulse flex-col items-center justify-center space-y-0.5">
                <span class="lnr icon-hourglass !text-4xl !text-blue-500 mb-2 inline-block w-8 text-center"></span>
                <span class="text-base font-medium">Loading graph data</span>
                <span class="text-sm text-gray-500">Just a moment…</span>
            </div>
        </div>

    </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref, computed} from "vue";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import {amchartsMixin} from "@/mixins/amchartsMixin";

amchartsMixin.methods.initializeAM5Charts();

const props = defineProps({
    meter: {
        type: Object,
        required: true
    },
    alarm: {
        type: Object,
        required: true
    }
});

const loading = ref(true);
const chartData = ref([]);
let alarmingIndex = -1;
let root;

const valueKey = computed(() => {
    const points = props.meter?.data;
    if (! points || Object.keys(points).length === 0) {
       return '';
    }

    const point = Object.values(points)[0];
    if ('mA' in point) return 'mA';
    if ('dBLevel' in point) return 'dBLevel';
    if ('data' in point) return 'data';

    return '';
});

const timeKey = computed(() => {
    const points = props.meter?.data;
    if (! points || Object.keys(points).length === 0) {
        return '';
    }

    const point = Object.values(points)[0];
    if ('Time' in point) return 'Time';
    if ('time' in point) return 'time';
    return 'Stamp';
});

const formatDate = (d) => {
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${hour12}:${minutes} ${ampm}`;
};

const buildChartData = () => {
    if (! valueKey.value || ! timeKey.value) {
        return;
    }

    alarmingIndex = -1;
    let smallestDiff = Infinity;

    try {
        chartData.value = Object.keys(props.meter.data).map((key, index) => {
            const dataPoint = props.meter.data[key];
            const time = dataPoint[timeKey.value];

            let timestamp;
            if (typeof time === 'number') {
                timestamp = time * 1000;
            } else {
                timestamp = new Date(time).getTime();
            }

            const alarmTimeMs = props.alarm.Time * 1000;
            const diff = Math.abs(timestamp - alarmTimeMs);

            if (diff < smallestDiff) {
                smallestDiff = diff;
                alarmingIndex = index;
            }
            return {
                date: timestamp,
                value: parseFloat(dataPoint[valueKey.value].toFixed(1)),
                low_alarm: props.alarm.alarm_profile.low_alarm_value,
                high_alarm: props.alarm.alarm_profile.high_alarm_value,
                low_caution: props.alarm .alarm_profile.low_caution_value,
                high_caution: props.alarm.alarm_profile.high_caution_value,
                caution_time: props.alarm.alarm_profile.high_caution_time,
            };
        });
    } catch (err) {
        console.error(`[Alarm Point Graph]\nThere was an error when building the chart data for meter ${props.meter?.SerialNumber || '[unknown]'}:`, err);
    }
};

onMounted(async () => {

    if (Object.keys(props.meter.data).length === 0) {
        console.error(`[Alarm Point Graph]\nChart Error: No usable data returned for meter ${props.meter?.SerialNumber || '[unknown]'}.`);
        loading.value = false;
        return
    }

    buildChartData();

    if (chartData.value.length === 0) {
        loading.value = false;
        return;
    }

    root = am5.Root.new('alarm-chart');

    let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            paddingTop: 32,
            paddingBottom: 32,
            paddingLeft: 32,
            paddingRight: 32
        })
    );

    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            strictMinMax: true,
            renderer: am5xy.AxisRendererY.new(root, {
                minGridDistance: 20
            }),
            visible: true
        })
    );
    yAxis.get('renderer').grid.template.set('forceHidden', true); // This hides the grid lines
    yAxis.get("renderer").labels.template.setAll({
        fontSize: 10,
        fill: am5.color('#6B7280')
    });
    yAxis.get("renderer").labels.template.adapters.add("text", (text) => {
        return `${text} ${props.meter.unit}`;
    });

    let xAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
            baseInterval:{ count: 1, timeUnit: 'second' }, // When doing a date axis, you need to set the base interval
            renderer: am5xy.AxisRendererX.new(root, {}),
            visible: false // Hide the labels
        })
    );
    xAxis.get('renderer').grid.template.set('forceHidden', true); // This hides the grid lines

    let createSeries = (name, field, settings) => {
        let series = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: name,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: field,
                valueXField: "date",
                stroke: settings.stroke,
            })
        );
        series.strokes.template.setAll({
            ...settings
        });
        series.data.setAll(chartData.value);
    }

    const thresholds = [
        { key: 'high_alarm', label: 'High Alarm', color: '#991b1b' },
        { key: 'low_alarm', label: 'Low Alarm', color: '#991b1b' },
        { key: 'high_caution', label: 'High Caution', color: '#eab308', dashed: true },
        { key: 'low_caution', label: 'Low Caution', color: '#eab308', dashed: true }
    ];
    thresholds.forEach(({ key, label, color, dashed }) => {
        if (chartData.value[0][key] !== null && chartData.value[0][key] !== undefined) {
            createSeries(label, key, {
                stroke: am5.color(color),
                tensionX: 1,
                strokeWidth: 1,
                strokeDasharray: dashed ? [4, 4] : undefined
            });
        }
    });

    createSeries('Value', 'value', {
        stroke: am5.color('#3d81f7'),
        tensionX: 0.8,
        strokeWidth: 2,
    });

    const alarmIndex = alarmingIndex !== -1 ? alarmingIndex : Math.floor(chartData.value.length / 2);
    const alarmTimestamp = chartData.value[alarmIndex].date;

    let bulletSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: "date",
            valueYField: "value",
        })
    );

    bulletSeries.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: am5.Line.new(root, {
                height: am5.percent(200),
                stroke: am5.color('#9b1b1b'),
                strokeWidth: 10,
                opacity: 0.2,
                rotation: 0,
                centerX: am5.percent(50),
                centerY: am5.percent(50),
            })
        });
    });

    bulletSeries.data.setAll([{ date: alarmTimestamp, value: chartData.value[alarmIndex].value }]);

    let labelContainer = chart.children.unshift(am5.Container.new(root, {
        width: am5.percent(100),
        paddingLeft: 0,
        paddingRight: 0
    }));

    let firstDate = new Date(chartData.value[0].date)
    let lastDate = new Date(chartData.value[chartData.value.length - 1].date)

    labelContainer.children.unshift(am5.Label.new(root, {
        text: formatDate(firstDate),
        fontSize: 10,
        fill: am5.color('#111827'),
        textAlign: "left",
        x: am5.percent(0),
        paddingLeft: 0,
        paddingBottom: 10
    }));

    labelContainer.children.unshift(am5.Label.new(root, {
        text: formatDate(lastDate),
        fontSize: 10,
        fill: am5.color('#111827'),
        textAlign: "right",
        x: am5.percent(100),
        paddingBottom: 10,
        paddingRight: 0
    }));

    loading.value = false;
});

onUnmounted(() => {
    if (root) root.dispose();
});
</script>
